import type { ReviewQueueGroups } from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Loader2, RefreshCw } from 'lucide-react';

interface Props {
  counts?: Record<keyof ReviewQueueGroups, number>;
  onReRun: () => void;
  isRunning: boolean;
}

export function StatsPanel({ counts, onReRun, isRunning }: Props) {
  // "Needs work" = items still awaiting reviewer action. Excludes already-
  // resolved categories (Matched, Transfers — both already paired) and
  // documentsWithoutPayment (informational; lives on the docs page).
  const needsWork = counts
    ? counts.suggestedMatches +
      counts.unmatchedTransactions +
      counts.missingDocuments +
      counts.ambiguousVendor +
      counts.amountMismatch +
      counts.multipleCandidates +
      counts.possibleDuplicates
    : 0;
  return (
    <div className="rounded-md border border-surface-200 bg-surface-0 p-4">
      <h3 className="text-sm font-semibold text-surface-900 mb-3">Reconciliation</h3>
      <dl className="space-y-2 text-sm">
        <Row label="Needs work" value={needsWork} bold />
        <Row label="Matched" value={counts?.autoMatched ?? 0} />
        <Row label="Suggested" value={counts?.suggestedMatches ?? 0} />
        <Row label="Unmatched txn" value={counts?.unmatchedTransactions ?? 0} />
        <Row label="Missing docs" value={counts?.missingDocuments ?? 0} />
        <Row label="Ambiguous vendor" value={counts?.ambiguousVendor ?? 0} />
        <Row label="Amount mismatch" value={counts?.amountMismatch ?? 0} />
        <Row label="Multiple candidates" value={counts?.multipleCandidates ?? 0} />
        <Row label="Transfers" value={counts?.transfers ?? 0} />
        <Row label="Possible dupes" value={counts?.possibleDuplicates ?? 0} />
      </dl>
      <Button onClick={onReRun} disabled={isRunning} className="mt-4 w-full" size="sm">
        {isRunning ? (
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
        ) : (
          <RefreshCw className="h-4 w-4 mr-2" />
        )}
        {isRunning ? 'Running engine…' : 'Re-run reconciliation'}
      </Button>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: number;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-surface-500">{label}</dt>
      <dd className={bold ? 'font-semibold' : ''}>{value}</dd>
    </div>
  );
}
